import { captureError } from './capture';

export const getUrl = (hostname?: string, path = ''): string => {
  if (!hostname) {
    return path[0] === '/' ? path : `/${path}`;
  }

  return new URL(path, hostname).toString();
};

export const parseQueryParam = <T extends string>(
  url: string,
  param: string,
  fallbackValue: T,
): T => {
  try {
    const params = new URL(url).searchParams;
    return params.has(param)
      ? (decodeURIComponent(params.get(param) as string) as T)
      : fallbackValue;
  } catch (err) {
    captureError(err);
    return fallbackValue;
  }
};

// makes sure an href has an absolute path
export const absoluteHref = (uri: string) => {
  // if it has a protocol it is an absolute path
  if (uri.includes('//')) {
    return uri;
  }
  return `//${uri}`;
};

export const stripTrailingSlash = (uri: string) => {
  if (uri.endsWith('/')) {
    return uri.slice(0, -1);
  }
  return uri;
};

export const getUrlHost = (str: string, trim?: string) => {
  try {
    const url = new URL(str);
    const { host } = url;

    if (trim && host.startsWith(trim)) {
      return host.replaceAll(trim, '');
    }

    return url.host;
  } catch (err) {
    return str;
  }
};

const absolutePattern =
  /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/.*)?$/i;

export const isAbsoluteUrl = (url: string): boolean => {
  return absolutePattern.test(url);
};

export const isValidUrl = (url: string): boolean => {
  try {
    if (isAbsoluteUrl(url)) {
      new URL(url);
      return true;
    }

    const relativeUrlPattern = /^\/[^\s]*$/;
    return relativeUrlPattern.test(url);
  } catch {
    return false;
  }
};

export const isValidInstagramUrl = (url: string) => {
  const originRegex = /^https?:\/\/(?:www\.)?(?:instagram\.com|instagr\.am)$/;

  try {
    const u = new URL(url);
    return originRegex.test(u.origin);
  } catch {
    return false;
  }
};

export const isValidTwitterUrl = (url: string) => {
  const originRegex = /^https?:\/\/(?:www\.)?(twitter|x)\.com$/;

  try {
    const u = new URL(url);
    return originRegex.test(u.origin);
  } catch {
    return false;
  }
};

export const updateQueryParameters = (
  url: string,
  updates: Array<{ key: string; value: string | null }>,
): string => {
  const [baseUrl, queryString] = url.split('?');
  const params = new URLSearchParams(queryString || '');

  updates.forEach(({ key, value }) => {
    if (value !== null) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  });

  const newQueryString = params.toString();
  return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
};
